import React, { useRef, useCallback } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { DesignElement as DesignElementType, Breakpoint, GridSettings } from '../../../types/coupon';
import QRCode from 'qrcode.react';

// Get base URL for images from environment or default
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

interface ElementDimensions {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface DesignElementProps {
    element: DesignElementType;
    breakpoint: Breakpoint;
    selected: boolean;
    onClick: (event: React.MouseEvent) => void;
    onUpdate: (element: DesignElementType) => void;
    gridSettings: GridSettings;
}

export const DesignElement: React.FC<DesignElementProps> = ({
    element,
    breakpoint,
    selected,
    onClick,
    onUpdate,
    gridSettings
}) => {
    const theme = useTheme();
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: element.id,
        data: element
    });

    const position = element.responsive[breakpoint];
    const elementStyle = {
        position: 'absolute' as const,
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        transform: transform ? 
            `translate3d(${transform.x}px, ${transform.y}px, 0) rotate(${position.rotation || 0}deg)` : 
            `rotate(${position.rotation || 0}deg)`,
        ...element.style,
        ...(selected ? { outline: `2px solid ${theme.palette.primary.main}` } : {}),
        cursor: 'move',
        userSelect: 'none' as const,
        touchAction: 'none' as const,
        zIndex: selected ? 1000 : element.style?.zIndex || 0
    };

    const getImageUrl = (path: string) => {
        if (!path) return '';
        const cleanPath = path.replace(/^\/+/, '');
        const url = `${BASE_URL}/${cleanPath}`;
        return process.env.NODE_ENV === 'development' ? `${url}?t=${new Date().getTime()}` : url;
    };

    const renderElement = () => {
        switch (element.type) {
            case 'text':
                return (
                    <Typography
                        ref={setNodeRef}
                        component="div"
                        style={elementStyle}
                        onClick={onClick}
                        {...attributes}
                        {...listeners}
                    >
                        {element.content}
                    </Typography>
                );
            case 'image':
                return (
                    <Box
                        ref={setNodeRef}
                        component="div"
                        style={elementStyle}
                        onClick={onClick}
                        {...attributes}
                        {...listeners}
                        sx={{
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5'
                        }}
                    >
                        {element.content ? (
                            <img
                                src={getImageUrl(element.content)}
                                alt={element.alt || ''}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: element.style?.objectFit || 'contain'
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    color: 'text.secondary'
                                }}
                            >
                                <Typography variant="caption">No image selected</Typography>
                            </Box>
                        )}
                    </Box>
                );
            case 'qrcode':
                return (
                    <Box
                        ref={setNodeRef}
                        component="div"
                        style={{
                            ...elementStyle,
                            backgroundColor: '#ffffff',
                            border: '1px solid #e0e0e0'
                        }}
                        onClick={onClick}
                        {...attributes}
                        {...listeners}
                    >
                        <Box sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <QRCode
                                value={element.content || 'https://example.com'}
                                size={Math.min(position.width, position.height) * 0.8}
                                level="H"
                                includeMargin={false}
                                bgColor="#ffffff"
                                fgColor="#000000"
                            />
                        </Box>
                        {element.content && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    position: 'absolute',
                                    bottom: 4,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '90%',
                                    textAlign: 'center',
                                    color: 'text.secondary'
                                }}
                            >
                                {element.content}
                            </Typography>
                        )}
                    </Box>
                );
            case 'dynamic':
                return (
                    <Typography
                        ref={setNodeRef}
                        component="div"
                        style={{
                            ...elementStyle,
                            backgroundColor: '#f5f5f5',
                            border: '1px dashed #ccc'
                        }}
                        onClick={onClick}
                        {...attributes}
                        {...listeners}
                    >
                        {element.content}
                    </Typography>
                );
            case 'divider':
                return (
                    <Box
                        ref={setNodeRef}
                        component="div"
                        style={elementStyle}
                        onClick={onClick}
                        {...attributes}
                        {...listeners}
                        sx={{
                            backgroundColor: element.style?.backgroundColor || '#000000'
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return renderElement();
};

export default DesignElement;
